"use client";
import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { C_WHLOC_COOKIE } from "../../../../constants/cookies.js";
import { SEARCH_SERVICE_PROVIDERS } from "../../../../constants/grs.js";
import { HOME_WAREHOUSE_POSTAL_CODE_LOCAL_STORAGE_KEY } from "../../../../constants/localStorage.js";
import Cookie from "js-cookie";
import { useAgent } from "../../../../utils/agent/AgentProvider.js";
import { useStwContext } from "../../../../utils/stwInfo/hook.js";
import { AKAMAI_ZIP_CODE_LOCALE_VALIDATION_CONFIGURATION } from "../../../../utils/validations/akamaiZipCode.js";
import { forwardRef, useState, useImperativeHandle, useRef, useCallback, useEffect } from "react";
import "../../../Chrome/BrowseContext/ContextProvider.js";
import { useBrowseContext } from "../../../Chrome/BrowseContext/hook.js";
import { storeLocation, warehouse } from "../../../Chrome/BrowseContext/legacyCookieStorage.js";
import { WarehouseDrawer } from "../../WarehouseDrawer/index.js";
import { WarehouseSelectorUI } from "../WarehouseSelectorUI/index.js";
import { getPreferredWarehouse, getSortedWarehouses, canBeSetAsPreferredWarehouse } from "../../utils/warehouse.lib.js";
import { useLocalStorage } from "../../../../hooks/useLocalStorage/index.js";
import { useLocation } from "../../../../hooks/useLocation/index.js";
import { useSearchProvider } from "../../../../hooks/useSearchProvider/index.js";
import { useUpdatePreferredWarehouse } from "../../../../hooks/useUpdatePreferredWarehouse/index.js";
const COMPONENT_NAME = "WarehouseSelectorData";
const WarehouseSelectorData = forwardRef((props, ref) => {
  var _a, _b, _c;
  const {
    displayOpenHours,
    hideLabelWhenGRSEnabled,
    isGRSFeatureFlagEnabled,
    isGRSBetaReleaseFeatureFlagEnabled,
    locale,
    moduleSettings,
    drawerType = "FMW"
  } = props;
  const isCMW = drawerType === "CMW";
  const [open, setOpen] = useState(false);
  useImperativeHandle(ref, () => ({ openDrawer: () => setOpen(true) }), []);
  const updatePreferredWarehouse = useUpdatePreferredWarehouse();
  const [preferredWarehouse, setPreferredWarehouse] = useState(void 0);
  const [homeWarehouseLocalStorage, setHomeWarehouseLocalStorage] = useLocalStorage(
    HOME_WAREHOUSE_POSTAL_CODE_LOCAL_STORAGE_KEY,
    void 0
  );
  const shouldUpdateCookiesRef = useRef(false);
  const searchProvider = useSearchProvider({
    isGRSFeatureFlagEnabled,
    isGRSBetaReleaseFeatureFlagEnabled
  });
  const { agent } = useAgent();
  const { isInitialized, userLocation, warehouse: warehouse$1 } = useBrowseContext();
  const { stwMyWarehouse, stwPostalCode, updateStwInfo } = useStwContext();
  const drawerProperties = moduleSettings.properties[0];
  const validationSchema = (_a = AKAMAI_ZIP_CODE_LOCALE_VALIDATION_CONFIGURATION[locale]) == null ? void 0 : _a.validationSchema;
  const preferredLocation = useLocation(
    drawerProperties.defaultLocation.latitude,
    drawerProperties.defaultLocation.longitude,
    validationSchema,
    !!(stwPostalCode && isCMW)
  );
  const cWhlocCookieContent = Cookie.get(C_WHLOC_COOKIE);
  const showLabel = !(searchProvider === SEARCH_SERVICE_PROVIDERS.GRS && hideLabelWhenGRSEnabled);
  const clearStwLocalStorage = useCallback(() => {
    updateStwInfo(
      {
        stwMyWarehouse: void 0,
        stwPostalCode: void 0
      },
      { updateState: false }
    );
  }, [updateStwInfo]);
  useEffect(() => {
    if (!isCMW) return;
    window.addEventListener("beforeunload", clearStwLocalStorage);
    return () => window.removeEventListener("beforeunload", clearStwLocalStorage);
  }, [
    isCMW,
    stwMyWarehouse,
    stwPostalCode,
    updateStwInfo,
    clearStwLocalStorage
  ]);
  useEffect(() => {
    if (!isCMW) return;
    updateStwInfo({
      stwMyWarehouse: void 0,
      stwPostalCode: void 0
    });
  }, [isCMW, (_b = warehouse$1.nearestWarehouse) == null ? void 0 : _b.catalog]);
  useEffect(() => {
    const loadWarehouses = async () => {
      var _a2, _b2, _c2, _d, _e, _f;
      if (!agent || !preferredLocation) {
        return null;
      } else {
        const actualWarehouseCookieValue = warehouse.read();
        const preferredWarehouseCode = getPreferredWarehouse(
          actualWarehouseCookieValue == null ? void 0 : actualWarehouseCookieValue.nearestWarehouse
        );
        const sortedWarehouses = await getSortedWarehouses({
          agent,
          locale,
          location: preferredLocation,
          preferredWarehouse: preferredWarehouseCode
        });
        if (sortedWarehouses == null ? void 0 : sortedWarehouses.length) {
          if (isCMW && stwMyWarehouse) {
            const selectedWarehouse = sortedWarehouses.find(
              (curr) => curr.warehouseId === stwMyWarehouse
            );
            if (selectedWarehouse && (preferredWarehouse == null ? void 0 : preferredWarehouse.warehouseId) !== selectedWarehouse.warehouseId) {
              setPreferredWarehouse(selectedWarehouse);
            }
          } else if (preferredWarehouseCode && preferredWarehouseCode !== "undefined" && cWhlocCookieContent) {
            const contextWarehouse = sortedWarehouses.find(
              (curr) => curr.warehouseId === preferredWarehouseCode
            );
            if (contextWarehouse && canBeSetAsPreferredWarehouse(contextWarehouse)) {
              if ((preferredWarehouse == null ? void 0 : preferredWarehouse.warehouseId) !== (contextWarehouse == null ? void 0 : contextWarehouse.warehouseId)) {
                setPreferredWarehouse(contextWarehouse);
              }
            }
          } else {
            const validPreferredWarehouse = sortedWarehouses.filter(
              canBeSetAsPreferredWarehouse
            )[0];
            if (validPreferredWarehouse && ((_a2 = warehouse$1 == null ? void 0 : warehouse$1.nearestWarehouse) == null ? void 0 : _a2.catalog) !== `${validPreferredWarehouse.warehouseId}-wh`) {
              updatePreferredWarehouse({
                city: (_c2 = (_b2 = validPreferredWarehouse.name) == null ? void 0 : _b2[0]) == null ? void 0 : _c2.value,
                countryCode: (_d = validPreferredWarehouse.address) == null ? void 0 : _d.countryName,
                postalCode: (_e = validPreferredWarehouse.address) == null ? void 0 : _e.postalCode,
                territory: (_f = validPreferredWarehouse.address) == null ? void 0 : _f.territory,
                warehouseId: validPreferredWarehouse.warehouseId,
                warehouseDrawer: drawerType,
                shouldUpdateCookies: shouldUpdateCookiesRef.current
              });
              shouldUpdateCookiesRef.current = false;
            }
            if (validPreferredWarehouse && (preferredWarehouse == null ? void 0 : preferredWarehouse.warehouseId) !== validPreferredWarehouse.warehouseId) {
              setPreferredWarehouse(validPreferredWarehouse);
            }
          }
        }
      }
    };
    loadWarehouses();
  }, [
    agent,
    cWhlocCookieContent,
    preferredLocation,
    locale,
    warehouse$1.nearestWarehouse,
    (_c = warehouse$1.nearestWarehouse) == null ? void 0 : _c.catalog,
    stwMyWarehouse,
    updatePreferredWarehouse
  ]);
  useEffect(() => {
    var _a2;
    if (!isInitialized) {
      return;
    }
    if (!userLocation.zip && (homeWarehouseLocalStorage == null ? void 0 : homeWarehouseLocalStorage.postalCode) || storeLocation.storeLocCookieExpResetFlag() !== "true") {
      userLocation.updateUserLocation({
        ...userLocation,
        zip: homeWarehouseLocalStorage == null ? void 0 : homeWarehouseLocalStorage.postalCode
      });
      shouldUpdateCookiesRef.current = true;
    }
    if (!((_a2 = warehouse$1.nearestWarehouse) == null ? void 0 : _a2.catalog) && (homeWarehouseLocalStorage == null ? void 0 : homeWarehouseLocalStorage.id)) {
      warehouse$1.updateWarehouse({
        nearestWarehouse: {
          ...warehouse$1.nearestWarehouse,
          catalog: homeWarehouseLocalStorage.id
        }
      });
      shouldUpdateCookiesRef.current = true;
    }
  }, [isInitialized]);
  useEffect(() => {
    if (!isInitialized) {
      return;
    }
    const preferredWarehouseContext = getPreferredWarehouse(
      warehouse$1.nearestWarehouse
    );
    const preferredWarehouseLocalStorage = getPreferredWarehouse({
      catalog: homeWarehouseLocalStorage == null ? void 0 : homeWarehouseLocalStorage.id
    });
    const shouldUpdateLocalStorage = preferredWarehouseContext && preferredWarehouseContext !== preferredWarehouseLocalStorage || (userLocation == null ? void 0 : userLocation.zip) && (userLocation == null ? void 0 : userLocation.zip) !== (homeWarehouseLocalStorage == null ? void 0 : homeWarehouseLocalStorage.postalCode);
    if (shouldUpdateLocalStorage) {
      setHomeWarehouseLocalStorage({
        ...preferredWarehouseContext && {
          id: `${preferredWarehouseContext}-wh`
        },
        ...(userLocation == null ? void 0 : userLocation.zip) && { postalCode: userLocation == null ? void 0 : userLocation.zip }
      });
    }
  }, [isInitialized, userLocation, warehouse$1]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      WarehouseSelectorUI,
      {
        displayOpenHours,
        moduleSettings,
        onClick: () => setOpen(true),
        preferredWarehouse,
        showLabel,
        drawerType,
        stockStatusData: props.stockStatusData,
        analyticsIdentifier: props.analyticsIdentifier
      }
    ),
    /* @__PURE__ */ jsx(
      WarehouseDrawer,
      {
        ...isCMW ? {
          drawerType: "CMW",
          childItemNumber: props.childItemNumber,
          productImageData: props.productImageData,
          productFlags: props.productFlags,
          batchServiceConfig: props.batchServiceConfig,
          featureConfigs: props.featureConfigs,
          enabledWarehouseListConfigs: props.enabledWarehouseListConfigs,
          analyticsIdentifier: props.analyticsIdentifier
        } : { drawerType: "FMW" },
        isOpen: open,
        locale,
        moduleSettings,
        onClose: () => setOpen(false),
        pageName: props.pageName
      }
    )
  ] });
});
WarehouseSelectorData.displayName = COMPONENT_NAME;
export {
  WarehouseSelectorData
};
